import { AfterViewInit, Directive, ElementRef, Input, OnDestroy } from '@angular/core';

interface StencilElement extends HTMLElement {
    componentOnReady?: () => Promise<unknown>;
}

/**
 * Applies a `data-qa` attribute to an element that lives inside another element's Shadow DOM.
 *
 * Some third party web components (e.g. the ACE `<ace-header>`) render their interactive elements
 * inside a shadow root and expose no class/id hook, so a `data-qa` tag cannot be added with a normal
 * template binding. This directive reaches into the host's shadow root, finds the element matching the
 * provided CSS selector and tags it.
 *
 * It waits for Stencil based components to finish hydrating (`componentOnReady`) and re-applies the tag
 * via a MutationObserver, because the target may be (re)created when the component re-renders.
 *
 * Example usage:
 * <ace-header wsShadowDataQa=".nav-primary ace-button" shadowDataQaValue="main-menu-hamburger"></ace-header>
 */
@Directive({
    selector: '[wsShadowDataQa]',
})
export class ShadowDataQaDirective implements AfterViewInit, OnDestroy {
    constructor(private readonly _elementRef: ElementRef<StencilElement>) {}

    /** CSS selector, evaluated against the host element's shadow root, identifying the element to tag */
    @Input('wsShadowDataQa') selector: string;

    /** The value to set for the `data-qa` attribute */
    @Input() shadowDataQaValue: string;

    private _observer: MutationObserver;

    async ngAfterViewInit(): Promise<void> {
        const host = this._elementRef.nativeElement;

        // Stencil custom elements only have a shadow root once they have hydrated
        if (typeof host.componentOnReady === 'function') {
            await host.componentOnReady();
        }

        this._applyTag();

        // The target may be created/destroyed on re-render (e.g. the hamburger only shows when nav is enabled),
        // so watch the shadow root and re-apply the tag whenever its content changes.
        if (host.shadowRoot) {
            this._observer = new MutationObserver(() => this._applyTag());
            this._observer.observe(host.shadowRoot, { childList: true, subtree: true });
        }
    }

    ngOnDestroy(): void {
        this._observer?.disconnect();
    }

    private _applyTag(): void {
        if (!this.selector || !this.shadowDataQaValue) {
            return;
        }

        const target = this._elementRef.nativeElement.shadowRoot?.querySelector(this.selector);
        if (target && target.getAttribute('data-qa') !== this.shadowDataQaValue) {
            target.setAttribute('data-qa', this.shadowDataQaValue);
        }
    }
}
